<template>
    <BaseLink
        :to="to"
        target="_self"
        :class="$style.verticalContainer"
        :title="`${props.categoryName} ${$tr('category.all_games')}`"
    >
        <div
            ref="game-cards-container"
            :class="[$style.grid, isSquareTypeCard && $style.square]"
        >
            <CardContainer
                is="div"
                v-for="(game, index) in props.games"
                :key="game?.title"
                :appearance="cardAppearance"
                :class="$style.gameCard_override"
                :alt="game?.title"
                :src="cardSrc(index)"
                responsive
                placeholder
                itemprop="image"
            />

            <div :class="['sqrcl', $style.blankRectangle, isContainerScreenCenter && $style.blankRectangleHighlighted]">
                <Icon
                    name="chevron-right"
                    :class="$style.chevronIcon"
                />
            </div>
        </div>
        <Typography
            v-if="props.type !== 'square-without-title'"
            type="label"
            size="xs"
            :class="$style.title"
            :responsive="false"
            :accent="true"
        >
            {{ $tr('category.all_games') }}
        </Typography>
    </BaseLink>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import CardContainer from '@/components/CardContainer.vue'
import Icon from '@/components/Icon/Icon.vue'
import Typography from '@/components/Typography.vue'
import type { ShortGameType } from '@/types'

const props = defineProps<{
    games: ShortGameType[] | undefined[]
    type: 'square' | 'square-without-title' | 'rectangle'
    to?: RouteLocationRaw
    categoryName?: string
}>()

const isSquareTypeCard = computed(() => props.type === 'square' || props.type === 'square-without-title')
const cardAppearance = computed(() => (isSquareTypeCard.value ? 'icon' : 'medium_horizontal'))
const cardSrc = (index: number) => (isSquareTypeCard.value && props.games[index]?.icon) || props.games[index]?.preview

const gameCardsContainer = useTemplateRef('game-cards-container')
const isContainerScreenCenter = ref(false)
const timeoutId = ref<number>()

onMounted(() => {
    const { stop } = useIntersectionObserver(
        gameCardsContainer,
        ([entry]) => {
            if (entry?.isIntersecting) {
                timeoutId.value = window.setTimeout(() => {
                    isContainerScreenCenter.value = true
                }, 1300)
                stop()
            }
        },
        { rootMargin: '0px 0px -30% 0px' },
    )
})

onUnmounted(() => {
    clearTimeout(timeoutId.value)
})
</script>

<style module>
.verticalContainer {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    position: relative;
    width: 100%;
    gap: 6px;
    transition: all 0.3s ease-in-out;

    &:hover .title {
        color: #d7b8ff;
    }

    &:hover .blankRectangle {
        background-image: linear-gradient(rgb(151, 71, 255) 0%, rgb(98, 15, 171) 100%);
    }
}

.grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    gap: 4px;
    box-sizing: border-box;
    border-radius: 12px;
    transition: all 0.3s ease-in-out;
    width: 100%;
    height: auto;
    aspect-ratio: var(--thumbnail-aspect-ratio);
}

.grid.square {
    aspect-ratio: 1;
}

.gameCard_override {
    aspect-ratio: auto;
}

.blankRectangle {
    background-color: #1d1c26;
    border-radius: 12px;
    --sqrcl: clamp(0rem, 30%, 50rem);
    display: flex;
    transition: background-color 0.3s ease-in-out;
    position: relative;
}

.blankRectangleHighlighted {
    background-color: var(--purple-400);
}

.chevronIcon {
    margin: auto;
    color: #ffffff;
}

.title {
    color: rgba(255, 255, 255, 0.63);
}
</style>
